'use client';

import { createPassSchema } from 'constant';
import { useFormik } from 'formik';

import { Box, Flex, useTheme, VStack } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setError } from 'store/reducers/userSlice';

import { AuthHeader } from 'components/AuthHeader';
import { AuthStepButtons } from 'components/AuthStepButtons';
import { PasswordRules } from 'components/PasswordRules';
import { IconTextField, Text } from 'ui';

const initialValues = {
  password: '',
  confirmPassword: ''
};

type ConfirmationPasswordFormProps = {
  onSubmit: ({ password }: { password: string }) => void;
  goBack: () => void;
  title?: string;
  description?: string;
};

const ConfirmationPasswordForm = ({
  title,
  description,
  onSubmit,
  goBack
}: ConfirmationPasswordFormProps) => {
  const dispatch = useAppDispatch();
  const { errorMsg } = useAppSelector((state) => state.user);
  const { values, dirty, isValid, setFieldValue, getFieldProps, handleSubmit } =
    useFormik({
      initialValues,
      validateOnMount: true,
      validateOnChange: true,
      validateOnBlur: true,
      validationSchema: createPassSchema,
      onSubmit
    });

  const {
    colors: {
      palette: {
        auth: {
          createAccount: { confirmationPassword }
        }
      }
    }
  } = useTheme();

  const isBtnDisabled = !dirty || !isValid;

  const handleClearErrors = () => {
    dispatch(setError(''));
  };

  const handleInputChange = (name: string) => (value: string) => {
    setFieldValue(name, value);

    if (errorMsg) {
      handleClearErrors();
    }
  };

  return (
    <VStack maxW={590}>
      {Boolean(title) && (
        <AuthHeader
          mb={{ base: '20px', md: '45px' }}
          title={title ?? ''}
          subtitle={description ?? ''}
          color={confirmationPassword.text.primary}
        />
      )}

      <Box
        as='form'
        onSubmit={handleSubmit}
        width='100%'
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
      >
        <Box alignItems='center' width='100%' h='full'>
          <Flex gap='25px' flexDirection={{ base: 'column', md: 'row' }}>
            <Flex flexDirection='column' h='full' w='full' flex={1}>
              <Flex
                flexDirection='column'
                alignItems='stretch'
                flex={1}
                gap='25px'
                position='relative'
                w='100%'
                justifyContent='space-between'
              >
                <IconTextField
                  autoFocus
                  {...getFieldProps('password')}
                  onChange={handleInputChange('password')}
                  type='password'
                  label='NEW PASSWORD'
                  ml={0}
                  borderRadius='5px'
                />
                <IconTextField
                  {...getFieldProps('confirmPassword')}
                  type='password'
                  onChange={handleInputChange('confirmPassword')}
                  label='CONFIRM PASSWORD'
                  ml={0}
                  borderRadius='5px'
                />
              </Flex>
            </Flex>

            <PasswordRules
              password={values.password}
              confirmPassword={values.confirmPassword}
              containerProps={{
                maxW: { base: '100%', md: '240px' }
              }}
              isAuthStep
            />
          </Flex>
          {errorMsg && (
            <Box w='100%' minHeight='20px' my={['11px', '16px', '17px']}>
              <Text
                w='100%'
                textAlign='center'
                color={confirmationPassword.text.error}
                bg={confirmationPassword.text.secondary}
              >
                {errorMsg}
              </Text>
            </Box>
          )}

          <AuthStepButtons
            mt={{ base: '40px', md: '50px' }}
            leftLabel='Back'
            rightLabel='Next'
            leftHandler={goBack}
            isBtnDisabled={isBtnDisabled}
          />
        </Box>
      </Box>
    </VStack>
  );
};

export default ConfirmationPasswordForm;
